import { useListCustomerByKeySearch } from "hooks/customer/useCustomer";
import { usePaymentMethodList } from "hooks/payment-method/usePaymentMethod";
import { useProductList } from "hooks/product/useProduct";
import { useEffect, useMemo } from "react";
import { BLOOD_COLLECTION_ORDER_FORM_ROLE_MODE } from ".";
import { useTimeslotCollectorList } from "hooks/timeslot-collector/useTimeslotCollector";
import { useCollectorLocationHub } from "hooks/order-collector/useOrderCollector";
import { useMyProfile, useSales } from "hooks";
import { useListHolidayWithTime } from "hooks/holiday/useHoliday";
import { filterNotFeatureShifts, filterValidShifts } from "./utils";

const useMasterData = ({
  mainForm,
  keywordCustomer,
  isViewOnlyMode,
  roleMode,
}) => {
  const { watch, setValue } = mainForm;
  const provinceCodeWatch = watch("province_code");
  const districtCodeWatch = watch("district_code");
  const wardCodeWatch = watch("ward_code");
  const actionDateWatch = watch("action_date");
  const customerWatch = watch("customer");

  const { data: collectorLocationHub, refetch: fetchCollectorLocationHub } =
    useCollectorLocationHub({
      params: {
        order_type: 3,
        province_code: provinceCodeWatch?.value,
        district_code: wardCodeWatch?.value,
        ward_code: null,
        expand: "hub",
      },
    });

  const { data: customers, refetch: fetchListCustomer } =
    useListCustomerByKeySearch({
      params: {
        page: -1,
        columnFilter: {
          "filter[record_status]": "O",
        },
        keywords: keywordCustomer.replace(" ", "+"),
      },
    });

  const {
    data: testPackList,
    refetch: fetchTestPack,
    isLoading: isTestPackLoading,
  } = useProductList({
    params: {
      page: -1,
      columnFilter: {
        "filter[record_status]": "O",
        "filter[visible]": 1,
      },
    },
  });

  const { data: paymentMethodList, refetch: fetchPaymentMethod } =
    usePaymentMethodList({
      params: {
        page: -1,
        columnFilter: {
          "filter[hub_id]": collectorLocationHub?.hub_id,
          "filter[record_status]": "O",
        },
      },
    });

  const { data: timeslotList, refetch: fetchTimeslotList } =
    useTimeslotCollectorList({
      params: {
        page: -1,
        columnFilter: {
          "filter[hub_id]": collectorLocationHub?.hub_id,
          "filter[record_status]": "O",
        },
      },
    });

  const {
    data: holidaysHub,
    refetch: fetchHolidays,
    remove: removeHolidays,
  } = useListHolidayWithTime({
    params: {
      columnFilter: {
        "filter[hub_id]": collectorLocationHub?.hub_id,
      },
      sizePerPage: 10,
      page: -1,
    },
    run: false,
    useToDisalbeCalendar: true,
    queryKey: "holiday-of-hub",
  });

  const timeslotOptions = useMemo(() => {
    if (
      timeslotList &&
      timeslotList?.items &&
      timeslotList?.items.length > 0 &&
      actionDateWatch
    ) {
      const todayShift = filterNotFeatureShifts(
        timeslotList.items,
        actionDateWatch
      );
      const validTimeslot = filterValidShifts(
        todayShift,
        holidaysHub?.items,
        actionDateWatch
      );

      return validTimeslot.map(item => ({
        label: item.name,
        value: item.id,
      }));
    }

    return [];
  }, [timeslotList, actionDateWatch, holidaysHub]);

  const {
    data: listSale,
    refetch: fetchListSale,
    isLoading: isLoadingListSale,
  } = useSales({
    customerId: customerWatch?.value
      ? JSON.parse(customerWatch?.value)?.id
      : undefined,
  });

  const { data: myProfile } = useMyProfile();

  useEffect(() => {
    if (!isViewOnlyMode) {
      fetchTestPack();
    }
  }, [isViewOnlyMode]);

  useEffect(() => {
    if (customerWatch?.value) {
      fetchListSale();
    }
  }, [customerWatch]);

  useEffect(() => {
    if (!isViewOnlyMode) {
      fetchListCustomer();
    }
  }, [keywordCustomer, isViewOnlyMode]);

  useEffect(() => {
    if (collectorLocationHub?.hub_id && !isViewOnlyMode) {
      setValue("from_hub", {
        label: collectorLocationHub?.hub_name,
        value: collectorLocationHub?.hub_id,
      });
      fetchPaymentMethod();
      fetchTimeslotList();
      fetchHolidays();
    }
  }, [collectorLocationHub?.hub_id, isViewOnlyMode]);

  useEffect(() => {
    if (provinceCodeWatch && wardCodeWatch && !isViewOnlyMode) {
      fetchCollectorLocationHub();
    }
  }, [provinceCodeWatch, wardCodeWatch, isViewOnlyMode]);

  useEffect(() => {
    if (
      roleMode === BLOOD_COLLECTION_ORDER_FORM_ROLE_MODE.SALE &&
      myProfile?.data?.id
    ) {
      setValue("sale", {
        label: myProfile?.data?.name,
        value: myProfile?.data?.id,
      });
    }
  }, [myProfile, roleMode]);

  return {
    customers,
    testPackList,
    isTestPackLoading,
    paymentMethodList,
    timeslotOptions,
    listSale,
    isLoadingListSale,
    collectorLocationHub,
    holidaysHub,
    timeslotList,
  };
};

export default useMasterData;
